// Mejorar resolución desde el lienzo — ADR-2026-09-16-las-herramientas-del-lienzo-abren-en-panel.
//
// El menú es EL MISMO que el del visor (`UpscaleMenu`, ADR-2026-09-16-los-menus-del-visor-son-componentes).
// Acá viven el guard de saldo antes de abrir, la llamada y qué hacer con el resultado:
// la mejora escribe `upscaled_path` sobre la MISMA fila —no hay imagen nueva que colgar
// del lienzo—, así que al terminar se avisa con el mismo toast del visor y se cierra.

import { useEffect, useState } from 'react';
import { sileo } from 'sileo';
import { UpscaleMenu } from '../../../components/UpscaleMenu';
import { mensajeDeUpscaleFallido, postImageUpscale } from '../../../lib/imageEdit';
import { supabase } from '../../../lib/supabase';
import { setCachedSignedUrl } from '../../../utils/imageCache';
import type { PropsDeHerramienta } from './herramienta';
import { consultarSaldo, tipoDeLimite } from './saldo';
import { useUrlDelOriginal } from './useUrlDelOriginal';

export const PanelMejorar: React.FC<PropsDeHerramienta> = ({ celda, onCerrar, onLimite, onError }) => {
  const original = useUrlDelOriginal(celda);
  const [abierto, setAbierto] = useState(false);
  const [escala, setEscala] = useState<2 | 4>(2);
  const [procesando, setProcesando] = useState(false);

  // Guard de créditos antes de abrir, con el costo de la escala inicial (como el visor).
  useEffect(() => {
    let vivo = true;
    void consultarSaldo(celda.companyId).then((s) => {
      if (!vivo) return;
      if (s.restantes !== null && s.restantes < 1) {
        onLimite(tipoDeLimite(s.plan));
        onCerrar();
        return;
      }
      setAbierto(true);
    });
    return () => {
      vivo = false;
    };
  }, [celda.companyId, onCerrar, onLimite]);

  const mejorar = async () => {
    if (procesando) return;
    setProcesando(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No pudimos identificar tu cuenta. Recargá la página.');
      const data = await postImageUpscale({ user_id: user.id, image_id: celda.imageId, scale: escala });
      if (data.success) {
        // El backend devuelve el path, no la URL: hay que firmarlo. El fallback es la
        // convención de siempre, la misma que usa el visor cuando la respuesta no lo trae.
        const path = data.upscaled_path || `${celda.companyId}/upscaled/${celda.datos.fileName}`;
        const { data: firmada } = await supabase.storage.from('generadas').createSignedUrl(path, 3600);
        if (firmada?.signedUrl) setCachedSignedUrl(path, firmada.signedUrl);
        sileo.success({
          title: 'Imagen mejorada',
          description: 'La versión mejorada quedó guardada en la imagen: la ves desde "Ver".',
        });
        onCerrar();
      } else if (data.error === 'insufficient_credits') {
        const s = await consultarSaldo(celda.companyId);
        onLimite(tipoDeLimite(s.plan));
        onCerrar();
      } else {
        onError(mensajeDeUpscaleFallido(data));
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'No pudimos procesar la imagen. Intentá nuevamente.');
    } finally {
      setProcesando(false);
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {}
    }
  };

  return (
    <UpscaleMenu
      open={abierto}
      imageUrl={original ?? celda.src}
      yaMejorada={!!celda.datos.upscaledPath}
      scale={escala}
      onScaleChange={setEscala}
      procesando={procesando}
      onClose={onCerrar}
      onConfirm={() => void mejorar()}
    />
  );
};
